import type { UIMessage } from 'ai';
import { RefObject, useEffect, useRef } from 'react';

const PIN_THRESHOLD = 80;

const measureContentLength = (message?: UIMessage): number => {
  if (!message) return 0;
  return message.parts.reduce((acc, part) => {
    if (part.type === 'text' || part.type === 'reasoning') {
      return acc + part.text.length;
    }
    const { data } = part as { data?: unknown };
    if (part.type.startsWith('data-') && typeof data === 'string') {
      return acc + data.length;
    }
    return acc;
  }, 0);
};

export const useChatScroll = (
  messages: UIMessage[],
): RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSignatureRef = useRef('');
  const lastCountRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  const isPinnedRef = useRef(true);
  const hasMessages = messages.length > 0;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      // A container without a layout box measures zero, which reads as pinned.
      if (container.clientHeight === 0) return;

      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      isPinnedRef.current = distanceFromBottom < PIN_THRESHOLD;
      lastScrollTopRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMessages]);

  // Hiding the container destroys its layout box, taking the scroll offset with it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => {
      if (container.clientHeight === 0) return;

      container.scrollTop = isPinnedRef.current
        ? container.scrollHeight
        : lastScrollTopRef.current;
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [hasMessages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const last = messages[messages.length - 1];
    const signature = `${messages.length}:${last?.id ?? ''}:${last?.parts.length ?? 0}:${measureContentLength(last)}`;
    if (signature === lastSignatureRef.current) return;
    lastSignatureRef.current = signature;

    const isNewMessage = messages.length > lastCountRef.current;
    lastCountRef.current = messages.length;

    const isOwnNewMessage = isNewMessage && last?.role === 'user';

    if (isOwnNewMessage || isPinnedRef.current) {
      container.scrollTop = container.scrollHeight;
      isPinnedRef.current = true;
    }
  }, [messages]);

  return containerRef;
};
